// Avatar data and the item catalog. One data object, two renderers (portrait, sprite).

export type Slot = 'top' | 'bottom' | 'shoes' | 'hat' | 'accessory' | 'back';

export interface AvatarData {
  body: { shape: 'a' | 'b' | 'c'; skin: string };
  hair: { style: string; color: string };
  eyes: string;
  mouth: string;
  top: string;
  bottom: string;
  shoes: string;
  hat?: string;
  accessory?: string;
  back?: string;
  persona: { name: string; favoriteColor: string; title?: string };
}

export const SKIN_TONES = ['#f6d7c3', '#e9b995', '#d49a6a', '#b5744a', '#8d5431', '#5c3a21'];
export const HAIR_COLORS = ['#2b1d12', '#5a3a1e', '#a0622d', '#d9a441', '#e6d27a', '#c0392b', '#7d5ba6', '#3b6fd6', '#2e9e8f', '#e884b0'];
export const PALETTE: Record<string, string> = {
  red: '#e2574c', orange: '#f08a3c', yellow: '#f5c542', green: '#5fbf6a', teal: '#3bb3a9',
  blue: '#4f8fe6', purple: '#9b6be6', pink: '#ee86b7', brown: '#8a5a3b', black: '#2a2a2e',
  white: '#f6f3ec', gray: '#9a9aa3',
};

export const HAIR_STYLES = ['short', 'bob', 'long', 'ponytail', 'curly', 'buns', 'spiky', 'braids'] as const;
export const EYE_STYLES = ['round', 'happy', 'wide', 'wink', 'sleepy', 'star'] as const;
export const MOUTH_STYLES = ['smile', 'grin', 'open', 'smirk'] as const;

export interface Item {
  id: string;
  slot: Slot;
  name: string;
  price: number;          // 0 = free starter
  color?: string;         // primary fill
  color2?: string;        // secondary fill
  style?: string;         // renderer hint ('stripes', 'hoodie', 'dress', ...)
}

export const ITEMS: Item[] = [
  // tops
  { id: 'top.tee.red', slot: 'top', name: 'Red tee', price: 0, color: PALETTE.red, style: 'tee' },
  { id: 'top.tee.blue', slot: 'top', name: 'Blue tee', price: 0, color: PALETTE.blue, style: 'tee' },
  { id: 'top.tee.green', slot: 'top', name: 'Green tee', price: 0, color: PALETTE.green, style: 'tee' },
  { id: 'top.stripes.purple', slot: 'top', name: 'Purple stripes', price: 40, color: PALETTE.purple, color2: PALETTE.white, style: 'stripes' },
  { id: 'top.stripes.teal', slot: 'top', name: 'Teal stripes', price: 40, color: PALETTE.teal, color2: PALETTE.yellow, style: 'stripes' },
  { id: 'top.hoodie.gray', slot: 'top', name: 'Gray hoodie', price: 60, color: PALETTE.gray, style: 'hoodie' },
  { id: 'top.hoodie.pink', slot: 'top', name: 'Pink hoodie', price: 60, color: PALETTE.pink, style: 'hoodie' },
  { id: 'top.overalls', slot: 'top', name: 'Overalls', price: 80, color: PALETTE.blue, color2: PALETTE.yellow, style: 'overalls' },
  { id: 'top.dress.yellow', slot: 'top', name: 'Sunny dress', price: 90, color: PALETTE.yellow, style: 'dress' },
  { id: 'top.dress.stars', slot: 'top', name: 'Star dress', price: 120, color: '#2d3a6b', color2: PALETTE.yellow, style: 'dress-stars' },
  { id: 'top.labcoat', slot: 'top', name: 'Lab coat', price: 150, color: PALETTE.white, color2: PALETTE.teal, style: 'coat' },
  { id: 'top.explorer', slot: 'top', name: 'Explorer vest', price: 150, color: '#b48a5a', color2: PALETTE.green, style: 'vest' },
  // bottoms
  { id: 'bottom.jeans', slot: 'bottom', name: 'Jeans', price: 0, color: '#3d5a99' },
  { id: 'bottom.shorts.green', slot: 'bottom', name: 'Green shorts', price: 0, color: PALETTE.green, style: 'shorts' },
  { id: 'bottom.leggings.purple', slot: 'bottom', name: 'Purple leggings', price: 30, color: PALETTE.purple },
  { id: 'bottom.skirt.red', slot: 'bottom', name: 'Red skirt', price: 50, color: PALETTE.red, style: 'skirt' },
  { id: 'bottom.skirt.rainbow', slot: 'bottom', name: 'Rainbow skirt', price: 110, color: PALETTE.pink, color2: PALETTE.teal, style: 'skirt-stripes' },
  { id: 'bottom.cargo', slot: 'bottom', name: 'Cargo pants', price: 60, color: '#6b7a4e', style: 'cargo' },
  // shoes
  { id: 'shoes.sneakers.white', slot: 'shoes', name: 'White sneakers', price: 0, color: PALETTE.white },
  { id: 'shoes.boots.brown', slot: 'shoes', name: 'Brown boots', price: 40, color: PALETTE.brown, style: 'boots' },
  { id: 'shoes.sneakers.red', slot: 'shoes', name: 'Red sneakers', price: 30, color: PALETTE.red },
  { id: 'shoes.rain.yellow', slot: 'shoes', name: 'Rain boots', price: 60, color: PALETTE.yellow, style: 'boots' },
  // hats
  { id: 'hat.cap.blue', slot: 'hat', name: 'Blue cap', price: 50, color: PALETTE.blue, style: 'cap' },
  { id: 'hat.beanie.red', slot: 'hat', name: 'Red beanie', price: 50, color: PALETTE.red, style: 'beanie' },
  { id: 'hat.crown', slot: 'hat', name: 'Crown', price: 200, color: PALETTE.yellow, style: 'crown' },
  { id: 'hat.explorer', slot: 'hat', name: 'Explorer hat', price: 120, color: '#b48a5a', style: 'brim' },
  { id: 'hat.wizard', slot: 'hat', name: 'Wizard hat', price: 220, color: PALETTE.purple, color2: PALETTE.yellow, style: 'wizard' },
  // accessories
  { id: 'acc.glasses.round', slot: 'accessory', name: 'Round glasses', price: 60, color: PALETTE.black, style: 'glasses' },
  { id: 'acc.bow.pink', slot: 'accessory', name: 'Pink bow', price: 40, color: PALETTE.pink, style: 'bow' },
  { id: 'acc.scarf.teal', slot: 'accessory', name: 'Teal scarf', price: 70, color: PALETTE.teal, style: 'scarf' },
  { id: 'acc.goggles', slot: 'accessory', name: 'Science goggles', price: 90, color: PALETTE.orange, style: 'goggles' },
  // back
  { id: 'back.cape.red', slot: 'back', name: 'Red cape', price: 150, color: PALETTE.red, style: 'cape' },
  { id: 'back.wings', slot: 'back', name: 'Little wings', price: 250, color: PALETTE.white, style: 'wings' },
  { id: 'back.backpack', slot: 'back', name: 'Backpack', price: 80, color: PALETTE.green, style: 'backpack' },
];

export const ITEM_BY_ID: Record<string, Item> = Object.fromEntries(ITEMS.map(i => [i.id, i]));
export const STARTER_ITEMS = ITEMS.filter(i => i.price === 0).map(i => i.id);

export function defaultAvatar(): AvatarData {
  return {
    body: { shape: 'a', skin: SKIN_TONES[1] },
    hair: { style: 'bob', color: HAIR_COLORS[1] },
    eyes: 'round',
    mouth: 'smile',
    top: 'top.tee.blue',
    bottom: 'bottom.jeans',
    shoes: 'shoes.sneakers.white',
    persona: { name: 'Explorer', favoriteColor: 'teal' },
  };
}
